// Kopfkarte der Schadenmappe (Bereich „Bearbeitung“): Bild, Schadensart –
// Kontakt, die drei Nummern, Schaden- und Meldetag, Schadensart/Risiko/Vertrag/
// Schadenhöhe, Beschreibung und Ursache. Oben rechts die drei Status „VN am
// Zug“, „VR am Zug“, „RS intern“ mit je einer Terminwiedervorlage (TWV).
import * as React from "react";
import type { BigAnswers } from "../../schadenmeldung-big/types";
import type { AbschlussWerte } from "../meldung/AbschlussIntern";
import type { DamagePatch, MappeDamage } from "../types";
import { hinweisen } from "../../redesign/bestaetigen";
import {
  betragAusText,
  betragText,
  bildVerkleinern,
  hoeheFeld,
  tagAus,
  type AmZug,
  type MappeZusatz,
} from "./mappeDaten";

export interface KopfkarteProps {
  damage: MappeDamage;
  answers: BigAnswers;
  /** Setzt Antworten der Meldung (gespeichert vom Meldungs-Autosave). */
  onAntworten: (patch: Partial<BigAnswers>) => void;
  abschluss: AbschlussWerte;
  onAbschluss: (patch: Partial<AbschlussWerte>) => void;
  /** Teil-PUT des Schadens. */
  onPatch: (patch: DamagePatch) => void | Promise<void>;
  mappe: MappeZusatz;
  onMappe: (patch: Partial<MappeZusatz>) => void;
}

const STATUS: Array<{ key: AmZug; label: string; feld: "wvVnAmZug" | "wvVrAmZug" | "wvRsIntern" }> = [
  { key: "vn", label: "VN am Zug", feld: "wvVnAmZug" },
  { key: "vr", label: "VR am Zug", feld: "wvVrAmZug" },
  { key: "rs", label: "RS intern", feld: "wvRsIntern" },
];

/** Ohne ausdrückliche Wahl gilt der Status mit der frühesten Wiedervorlage. */
function aktiverStatus(damage: MappeDamage, mappe: MappeZusatz): AmZug | null {
  if (mappe.amZug) return mappe.amZug;
  let bester: AmZug | null = null;
  let tag = "";
  for (const s of STATUS) {
    const t = tagAus(damage[s.feld]);
    if (t && (!tag || t < tag)) {
      tag = t;
      bester = s.key;
    }
  }
  return bester;
}

/** Textfeld, das erst beim Verlassen speichert (ein PUT je Eingabe, nicht je Taste). */
function Feld({
  label,
  wert,
  onSpeichern,
  typ = "text",
  mehrzeilig = false,
  nurLesen = false,
  platzhalter,
}: {
  label: string;
  wert: string;
  onSpeichern?: (wert: string) => void;
  typ?: "text" | "date";
  mehrzeilig?: boolean;
  nurLesen?: boolean;
  platzhalter?: string;
}) {
  const [lokal, setLokal] = React.useState(wert);
  const [fokus, setFokus] = React.useState(false);
  // Von außen nachgeführter Wert (Server-Antwort) — nicht während der Eingabe.
  React.useEffect(() => {
    if (!fokus) setLokal(wert);
  }, [wert, fokus]);

  const fertig = () => {
    setFokus(false);
    if (typ !== "date" && lokal !== wert) onSpeichern?.(lokal);
  };
  const gemeinsam = {
    value: lokal,
    readOnly: nurLesen,
    placeholder: platzhalter,
    onFocus: () => setFokus(true),
    onBlur: fertig,
  };
  return (
    <label className={"smb-fld" + (nurLesen ? " ist-fest" : "")}>
      <span className="smb-lbl">{label}</span>
      {mehrzeilig ? (
        <textarea {...gemeinsam} onChange={(e) => setLokal(e.target.value)} />
      ) : (
        <input
          {...gemeinsam}
          type={typ}
          onChange={(e) => {
            setLokal(e.target.value);
            // Ein Datum aus dem Kalender ist sofort fertig.
            if (typ === "date" && e.target.value !== wert) onSpeichern?.(e.target.value);
          }}
        />
      )}
    </label>
  );
}

/** Terminwiedervorlage eines Status — lokal geführt, damit die Eingabe nicht auf die Server-Antwort wartet. */
function Twv({ label, wert, onSpeichern }: { label: string; wert: string; onSpeichern: (wert: string) => void }) {
  const [lokal, setLokal] = React.useState(wert);
  React.useEffect(() => setLokal(wert), [wert]);
  return (
    <input
      type="date"
      aria-label={`Terminwiedervorlage ${label}`}
      title="Terminwiedervorlage (TWV)"
      value={lokal}
      onChange={(e) => {
        setLokal(e.target.value);
        if (e.target.value !== wert) onSpeichern(e.target.value);
      }}
    />
  );
}

function Wert({ label, haupt, neben }: { label: string; haupt?: string | null; neben?: string | null }) {
  return (
    <div className="smb-wertfeld">
      <span className="smb-lbl">{label}</span>
      <div className="smb-wert">
        {haupt || "–"}
        {neben ? <small>{neben}</small> : null}
      </div>
    </div>
  );
}

export function Kopfkarte({ damage, answers, onAntworten, abschluss, onAbschluss, onPatch, mappe, onMappe }: KopfkarteProps) {
  const aktiv = aktiverStatus(damage, mappe);
  // Mit einer Meldung aus dem Wizard leitet der Server Schadentag und -höhe aus
  // ihr ab — dann wird dort geändert, sonst direkt an der Spalte des Schadens.
  const ausMeldung = answers.damageType != null;
  const hoehe = hoeheFeld(answers);
  const antworten = answers as unknown as Record<string, unknown>;

  const schadentag = ausMeldung ? tagAus(answers.schadenDatum) || tagAus(damage.damageDate) : tagAus(damage.damageDate);
  const setSchadentag = (tag: string) => {
    if (ausMeldung) {
      const zeit = /T(\d{2}:\d{2})/.exec(answers.schadenDatum ?? "")?.[1] ?? "00:00";
      onAntworten({ schadenDatum: tag ? `${tag}T${zeit}` : "" });
    } else {
      void onPatch({ damageDate: tag || null });
    }
  };

  const hoeheText =
    hoehe && typeof antworten[hoehe] === "string" && antworten[hoehe] !== ""
      ? String(antworten[hoehe])
      : betragText(damage.claimAmount);
  const setHoehe = (text: string) => {
    if (hoehe) onAntworten({ [hoehe]: text } as Partial<BigAnswers>);
    else void onPatch({ claimAmount: betragAusText(text) });
  };

  const bildWahl = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    e.target.value = "";
    if (!datei) return;
    try {
      onMappe({ bild: await bildVerkleinern(datei) });
    } catch (fehler) {
      void hinweisen({
        titel: "Bild nicht übernommen",
        text: fehler instanceof Error ? fehler.message : String(fehler),
        ton: "danger",
      });
    }
  };

  const risiko = damage.riskObject as (MappeDamage["riskObject"] & { riskObjectIdentifier?: string | null }) | null | undefined;
  const vertrag = damage.contract as (MappeDamage["contract"] & { insuranceNbr?: string | null }) | null | undefined;
  const vertragNeben = [vertrag?.contractNbr ? `Nr. ${vertrag.contractNbr}` : "", vertrag?.insuranceNbr ? `VS-Nr. ${vertrag.insuranceNbr}` : ""]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="smb-karte" aria-label="Schaden">
      {/* Zeile 1: links das Bild, rechts die drei Status — zur Bildmitte zentriert. */}
      <div className="smb-kopf1">
        <div className="smb-bildspalte">
          <label
            className={"smb-bild" + (mappe.bild ? " hat-bild" : "")}
            style={mappe.bild ? { backgroundImage: `url(${mappe.bild})` } : undefined}
            title={mappe.bild ? "Bild ersetzen" : "Bild hinzufügen"}
          >
            {mappe.bild ? <span className="smp-sr">Bild ersetzen</span> : "Bild hinzufügen"}
            <input type="file" accept="image/*" hidden onChange={(e) => void bildWahl(e)} />
          </label>
          {mappe.bild && (
            <button type="button" className="smb-link" onClick={() => onMappe({ bild: null })}>
              Bild entfernen
            </button>
          )}
        </div>
        <div className="smb-status" role="group" aria-label="Status und Wiedervorlage">
          {STATUS.map((s) => (
            <div key={s.key} className={`smb-st ist-${s.key}` + (aktiv === s.key ? " ist-an" : "")}>
              <button
                type="button"
                className="smb-st-knopf"
                aria-pressed={aktiv === s.key}
                onClick={() => onMappe({ amZug: s.key })}
              >
                {s.label}
              </button>
              <Twv
                label={s.label}
                wert={tagAus(damage[s.feld])}
                onSpeichern={(w) => void onPatch({ [s.feld]: w || null } as DamagePatch)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="smb-zeile">
        <Feld label="Nr. intern" wert={damage.damageNbr ?? ""} nurLesen />
        <Feld
          label="Nr. VR"
          wert={abschluss.externalDamageNbr}
          onSpeichern={(w) => onAbschluss({ externalDamageNbr: w })}
        />
        <Feld
          label="Nr. Kunde"
          wert={mappe.nrKunde}
          onSpeichern={(w) => onMappe({ nrKunde: w })}
          platzhalter="Nummer beim Kunden"
        />
        <Feld label="Schadentag" typ="date" wert={schadentag} onSpeichern={setSchadentag} />
        <Feld
          label="Meldetag"
          typ="date"
          wert={tagAus(abschluss.reportDate)}
          onSpeichern={(w) => onAbschluss({ reportDate: w || null })}
        />
      </div>

      <div className="smb-zeile">
        <Wert label="Schadensart" haupt={damage.damageType} neben={damage.kaskoart} />
        <Wert label="Risiko" haupt={risiko?.name} neben={risiko?.riskObjectIdentifier || risiko?.riskType} />
        <Wert label="Vertrag" haupt={vertrag?.branch || vertrag?.contractNbr} neben={vertrag?.branch ? vertragNeben : null} />
        <Feld label="Schadenhöhe (€)" wert={hoeheText} onSpeichern={setHoehe} />
      </div>

      <div className="smb-zeile smb-zeile-breit">
        <Feld
          label="Schadenbeschreibung"
          mehrzeilig
          wert={damage.description ?? ""}
          onSpeichern={(w) => void onPatch({ description: w })}
        />
        <Feld label="Schadenursache" mehrzeilig wert={mappe.ursache} onSpeichern={(w) => onMappe({ ursache: w })} />
      </div>
    </section>
  );
}
